import { useState, useEffect, useCallback } from 'react';
import {
  BuildingOfficeIcon,
  HomeModernIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../components/ui/chart';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useTranslation } from '../../i18n';
import {
  fetchGlobalDashboardStats,
  fetchDashboardTrends,
  fetchHotels,
  GlobalDashboardStats,
  TrendDataPoint,
} from '../../services/hotelAdminService';

const checkinsTrendConfig = {
  checkins: { label: 'Check-ins', color: 'hsl(var(--chart-1))' },
} satisfies ChartConfig;

export default function GlobalDashboardPage() {
  const { t } = useTranslation();
  const [stats, setStats] = useState<GlobalDashboardStats | null>(null);
  const [trends, setTrends] = useState<TrendDataPoint[]>([]);
  const [provinces, setProvinces] = useState<string[]>([]);
  const [selectedProvince, setSelectedProvince] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load province list from hotels
  useEffect(() => {
    fetchHotels({ limit: 500 })
      .then((res) => {
        const uniqueProvinces = Array.from(
          new Set((res.data || []).map((h) => h.province).filter(Boolean))
        ).sort();
        setProvinces(uniqueProvinces);
      })
      .catch(() => {
        // Non-blocking — provinces filter just won't be populated
      });
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const province = selectedProvince || undefined;
      const [statsData, trendsData] = await Promise.all([
        fetchGlobalDashboardStats(province),
        fetchDashboardTrends(province),
      ]);
      setStats(statsData);
      setTrends(trendsData.data_points || []);
    } catch (err: any) {
      setError(err.message || t('dashboard.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [selectedProvince, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const formatNumber = (n: number) =>
    new Intl.NumberFormat('fr-FR').format(n);

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'USD' }).format(n);

  const formatPercent = (n: number) => `${n.toFixed(1)}%`;

  const trendChartData = trends.map((point) => ({
    date: point.date.slice(5),
    checkins: point.checkins,
    occupancy: point.occupancy_rate,
  }));

  if (loading && !stats) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-28 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
          <div className="h-64 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <ChartBarIcon className="h-8 w-8 text-blue-600 mr-3" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('nav.globalDashboard')}</h1>
            <p className="text-gray-600 mt-1">
              {selectedProvince || t('dashboard.allProvinces')}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {/* Province filter */}
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-4 w-4 text-gray-500" />
            <select
              value={selectedProvince}
              onChange={(e) => setSelectedProvince(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">{t('dashboard.allProvinces')}</option>
              {provinces.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <button
            onClick={loadData}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
            title={t('dashboard.reload')}
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-3" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
          <button
            onClick={loadData}
            className="px-3 py-1 text-sm border border-red-300 rounded text-red-700 hover:bg-red-100"
          >
            {t('dashboard.reload')}
          </button>
        </div>
      )}

      {stats && (
        <>
          {/* Main stat cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <StatCard
              icon={<BuildingOfficeIcon className="h-8 w-8 text-blue-600" />}
              label="Établissements actifs"
              value={formatNumber(stats.active_hotels)}
              sub={`${formatNumber(stats.pending_hotels)} en attente KYC`}
            />
            <StatCard
              icon={<HomeModernIcon className="h-8 w-8 text-purple-600" />}
              label="Chambres (hôtels)"
              value={formatNumber(stats.hotel_rooms_total ?? stats.total_rooms)}
              sub={`${formatNumber(stats.occupied_rooms)} occupées`}
            />
            <StatCard
              icon={<UserGroupIcon className="h-8 w-8 text-green-600" />}
              label="Occupation hôtels"
              value={formatPercent(stats.global_occupancy_rate)}
              sub={`${formatNumber((stats.hotel_rooms_total ?? stats.total_rooms) - stats.occupied_rooms)} disponibles`}
            />
            <StatCard
              icon={<CurrencyDollarIcon className="h-8 w-8 text-orange-600" />}
              label={t('dashboard.fptRevenue')}
              value={formatCurrency(stats.total_fpt_revenue)}
              sub={`${formatNumber(stats.fpt_paid_count || 0)} à jour · ${formatNumber(stats.fpt_overdue_count || 0)} en retard`}
            />
          </div>

          {/* Sector breakdown — real counts from API */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Hôtels', value: stats.hotels_count ?? 0 },
              { label: 'Restaurants', value: stats.restaurants_count ?? 0 },
              { label: 'Lounges & Bars', value: stats.lounges_count ?? 0 },
              { label: 'Attractions', value: stats.attractions_count ?? 0 },
            ].map((c) => (
              <div key={c.label} className="bg-white rounded-lg shadow px-4 py-3">
                <p className="text-xs text-gray-500">{c.label}</p>
                <p className="text-xl font-bold text-gray-900">{formatNumber(c.value)}</p>
              </div>
            ))}
          </div>

          {/* Check-in stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <StatCard
              icon={<CalendarDaysIcon className="h-8 w-8 text-teal-600" />}
              label={t('dashboard.checkinsToday')}
              value={formatNumber(stats.checkins_today)}
            />
            <StatCard
              icon={<CalendarDaysIcon className="h-8 w-8 text-indigo-600" />}
              label={t('dashboard.checkinsWeek')}
              value={formatNumber(stats.checkins_this_week)}
            />
            <StatCard
              icon={<CalendarDaysIcon className="h-8 w-8 text-pink-600" />}
              label={t('dashboard.checkinsMonth')}
              value={formatNumber(stats.checkins_this_month)}
            />
          </div>

          {/* FPT Payment Summary */}
          {(stats.fpt_overdue_count > 0 || stats.fpt_total_due > 0) && (
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Situation FPT</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center space-x-3">
                  <span className="w-3 h-3 rounded-full bg-green-500 inline-block" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{formatNumber(stats.fpt_paid_count || 0)} établissements à jour</p>
                    <p className="text-xs text-gray-500">Paiements FPT complets</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="w-3 h-3 rounded-full bg-red-500 inline-block" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{formatNumber(stats.fpt_overdue_count || 0)} établissements en retard</p>
                    <p className="text-xs text-gray-500">Paiements FPT impayés</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Total dû: {formatCurrency(stats.fpt_total_due || 0)}</p>
                  <p className="text-xs text-gray-500">Collecté: {formatCurrency(stats.total_fpt_revenue)}</p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Trends chart */}
      {trends.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('dashboard.trends')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer config={checkinsTrendConfig} className="h-[220px] w-full">
              <BarChart data={trendChartData}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  interval={4}
                  tick={{ fontSize: 10 }}
                />
                <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 10 }} />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      formatter={(value, _name, item) => [
                        `${value} check-ins · ${item.payload.occupancy?.toFixed?.(1) ?? item.payload.occupancy}% occ.`,
                        'Activité',
                      ]}
                    />
                  }
                />
                <Bar dataKey="checkins" fill="var(--color-checkins)" radius={4} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className="flex-shrink-0">{icon}</div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-600">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
        </div>
      </div>
    </div>
  );
}
